import type { ElementType, ReactNode } from 'react'
import type { CSSVars } from '@/types/css'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface RevealProps {
    as?: ElementType
    variant?: 'fade' | 'wipe'
    delay?: number
    className?: string
    id?: string
    children: ReactNode
}

export const Reveal = ({
    as: Tag = 'div',
    variant = 'fade',
    delay = 0,
    className,
    id,
    children
}: RevealProps): ReactNode => {
    const { ref, inView } = useInView<HTMLElement>()
    const style: CSSVars = { '--reveal-delay': `${delay}ms` }

    return (
        <Tag
            ref={ref}
            id={id}
            data-reveal={variant === 'fade' ? '' : variant}
            className={cn(className, inView && 'is-revealed')}
            style={style}
        >
            {children}
        </Tag>
    )
}

interface RevealLinesProps {
    as?: ElementType
    lines: ReactNode[]
    delay?: number
    className?: string
    id?: string
}

export const RevealLines = ({
    as: Tag = 'h2',
    lines,
    delay = 0,
    className,
    id
}: RevealLinesProps): ReactNode => {
    const { ref, inView } = useInView<HTMLElement>()
    const style: CSSVars = { '--reveal-delay': `${delay}ms` }

    return (
        <Tag
            ref={ref}
            id={id}
            data-reveal='lines'
            className={cn(className, inView && 'is-revealed')}
            style={style}
        >
            {lines.map((line, index) => {
                const lineStyle: CSSVars = { '--line': index }

                return (
                    <span key={index} className='reveal-line' style={lineStyle}>
                        <span>
                            {line}
                        </span>
                    </span>
                )
            })}
        </Tag>
    )
}
